import * as Icons from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { DispositionDialog } from "@/components/DRLPanel";

/* Operational Cockpit — the ONE authoritative operational summary for a Lead/Client.
   Pure presentation: renders GET /api/workspace/:id/cockpit + DRL assessment state.
   Color language (platform standard): slate=context · green=current state · orange=next action */

const CONF = {
  high: { label: "High", cls: "text-emerald-700", bar: "bg-emerald-500", w: "w-full" },
  medium: { label: "Medium", cls: "text-amber-700", bar: "bg-amber-500", w: "w-2/3" },
  low: { label: "Low", cls: "text-rose-700", bar: "bg-rose-500", w: "w-1/3" },
};
const MS_TONE = {
  complete: "bg-emerald-500 text-white border-emerald-500",
  in_progress: "bg-amber-400 text-white border-amber-400",
  not_started: "bg-white text-slate-300 border-slate-200",
};
const RESP_BADGE = {
  STAFF:                "bg-blue-50 text-blue-700 border-blue-200",
  WAITING_ON_CLIENT:    "bg-amber-50 text-amber-700 border-amber-200",
  WAITING_ON_THIRD_PARTY: "bg-slate-50 text-slate-600 border-slate-200",
};
const RESP_LABEL = {
  STAFF:                "Staff action",
  WAITING_ON_CLIENT:    "Waiting on client",
  WAITING_ON_THIRD_PARTY: "Waiting on 3rd party",
};

// Lifecycle status pill colors
const LIFECYCLE_TONE = {
  lead: "bg-indigo-50 text-indigo-700 border border-indigo-200",
  active_client: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  inactive_client: "bg-slate-100 text-slate-500 border border-slate-200",
  active: "bg-emerald-50 text-emerald-700 border border-emerald-200",
};
const LIFECYCLE_LABEL = {
  lead: "Onboarding",
  active_client: "Active Client",
  inactive_client: "Inactive Client",
  active: "Active Client",
};

function JourneyBanner({ stage }) {
  const stages = stage?.stages || [];
  if (!stages.length) return null;
  const curIdx = stages.findIndex((s) => s.key === stage.current);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-3 mb-4 overflow-x-auto" data-testid="cockpit-journey-banner">
      <div className="flex items-center gap-1 min-w-max">
        {stages.map((s, i) => {
          const st = s.status || "not_started";
          const active = i === curIdx;
          return (
            <div key={s.key} className="flex items-center">
              <div className="flex flex-col items-center gap-1">
                <span className={`h-7 w-7 rounded-full border flex items-center justify-center text-[11px] font-bold ${MS_TONE[st]} ${active ? "ring-2 ring-offset-2 ring-blue-500" : ""}`}>
                  {st === "complete" ? <Icons.Check className="h-3.5 w-3.5" /> : i + 1}
                </span>
                <span className={`text-[10px] font-medium whitespace-nowrap ${active ? "text-slate-800" : "text-slate-400"}`}>{s.label}</span>
              </div>
              {i < stages.length - 1 && <span className={`h-0.5 w-8 mx-1 ${i < curIdx ? "bg-emerald-400" : "bg-slate-200"}`} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function QuestionCard({ n, tone, question, children, testid }) {
  const tones = {
    slate: { ring: "border-slate-200", bg: "bg-slate-50/60", num: "bg-slate-500", q: "text-slate-600" },
    green: { ring: "border-emerald-200", bg: "bg-emerald-50/50", num: "bg-emerald-500", q: "text-emerald-700" },
    orange: { ring: "border-orange-300", bg: "bg-orange-50", num: "bg-orange-500", q: "text-orange-700" },
  }[tone];
  return (
    <div className={`rounded-2xl border ${tones.ring} bg-white p-5 flex flex-col`} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <span className={`h-6 w-6 rounded-full ${tones.num} text-white text-xs font-bold flex items-center justify-center`}>{n}</span>
        <span className={`text-[11px] font-bold uppercase tracking-wider ${tones.q}`}>{question}</span>
      </div>
      <div className={`rounded-xl ${tones.bg} p-4 flex-1 flex flex-col`}>{children}</div>
    </div>
  );
}

function RailCard({ title, icon: Icon, action, children, testid }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />}{title}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

/* WorkstreamRow — compact single row for a secondary active workstream. */
function WorkstreamRow({ ws, navigate }) {
  const badgeCls = RESP_BADGE[ws.responsibility] || RESP_BADGE.STAFF;
  const respLabel = RESP_LABEL[ws.responsibility] || "Staff action";
  const key = ws.engagement_number || ws.engagement_id || ws.obligation_code;
  return (
    <div data-testid={`cockpit-workstream-row-${key}`}
      className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 px-4 py-3">
      {/* Scope identity */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-0.5">
          {ws.engagement_number && (
            <span className="text-[10px] font-semibold text-slate-400 shrink-0 tabular-nums">{ws.engagement_number}</span>
          )}
          <span className="text-sm font-semibold text-slate-800 truncate">{ws.scope_label}</span>
        </div>
        <div className="text-xs text-slate-500 truncate">
          {ws.current_state_label}
          {ws.next_action_label && <span className="text-slate-400"> — {ws.next_action_label}</span>}
        </div>
      </div>
      {/* Responsibility badge */}
      <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border shrink-0 ${badgeCls}`}>
        {respLabel}
      </span>
      {/* Navigate CTA */}
      <button
        data-testid={`cockpit-workstream-open-${key}`}
        onClick={() => navigate(ws.destination)}
        className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700 border border-blue-200 hover:border-blue-300 bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-1.5 transition-colors"
      >
        <Icons.ArrowRight className="h-3.5 w-3.5" />Open
      </button>
    </div>
  );
}

/* WorkstreamsSection — shown below the cockpit when a client has >1 active workstream. */
function WorkstreamsSection({ workstreams, navigate }) {
  const secondary = (workstreams || []).filter((w) => !w.is_primary);
  if (!secondary.length) return null;
  return (
    <div className="mt-4" data-testid="cockpit-other-workstreams">
      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-1.5">
        <Icons.Layers className="h-3.5 w-3.5" />Other Active Workstreams
      </div>
      <div className="space-y-2">
        {secondary.map((ws) => (
          <WorkstreamRow
            key={ws.engagement_id || ws.engagement_number || ws.obligation_code}
            ws={ws}
            navigate={navigate}
          />
        ))}
      </div>
    </div>
  );
}

export function OperationalCockpit({ data, drlData, clientId, onPrimaryAction, onSecondaryAction, onViewBriefing, onViewRouting, onResolveObligation, onRefresh, onDocumentAction }) {
  const navigate = useNavigate();
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  // Tracks the post-resolve "re-evaluating" phase: true from Resolve submit until drlData changes.
  const [resolving, setResolving] = useState(false);
  const drlDataRef = useRef(drlData);
  useEffect(() => {
    if (resolving && drlData !== drlDataRef.current) setResolving(false);
    drlDataRef.current = drlData;
  }, [drlData, resolving]);

  if (!data) return null;
  const cs = data.current_state || {}, goal = data.goal || {}, na = data.next_action || {};
  const reason = data.operational_reason || {}, conf = CONF[data.confidence?.level] || CONF.medium;
  const priority = data.priority || {};
  const mgmt = data.management_review || {};
  const secondaryAction = data.secondary_action || null;
  const workstreams = data.workstreams || [];
  const hasMultipleWorkstreams = workstreams.length > 1;
  const shouldShowScopeContext = workstreams.length > 1 || !!data.primary_scope_label;

  const ESTIMATE_OBLIGATION_CODES = new Set([
    "open_estimate", "review_estimate", "create_estimate",
    "finalize_estimate", "send_estimate",
  ]);
  const isEstimateObligation = ESTIMATE_OBLIGATION_CODES.has(data.obligation_code) && !!data.primary_engagement_id;

  // ── Card 3 state machine ─────────────────────────────────────────────────
  // Priority: resolving > loading > error > active_blocker > no_material_blocker
  // "resolving" keeps the card non-actionable while the new assessment loads after a Resolve.
  // "loading" (drlData === null) keeps it non-actionable during initial evaluation.
  // Never expose the cockpit CTA while the DRL result is unknown.
  const drlLoading    = drlData === null;
  const drlActive     = !resolving && !drlLoading && drlData?.assessment_type === "active_blocker";
  const drlError      = !resolving && !drlLoading && drlData?.assessment_type === "error";
  const drlClear      = !resolving && !drlLoading && drlData?.assessment_type === "no_material_blocker";
  // Document-missing blockers must not enter the generic DRL disposition path —
  // they belong to the document-waiver workflow (matches DRLPanel guard at line 441).
  const isDocumentBlocker = drlData?.blocker_category === "document_missing";

  // ── Card 2: current lifecycle stage from workflow_stage ──
  const wfStages  = data.workflow_stage?.stages || [];
  const wfCurrent = data.workflow_stage?.current || "lead";
  const currentStageEntry = wfStages.find((s) => s.key === wfCurrent);
  const currentStageLabel = currentStageEntry?.label || wfCurrent;
  const currentStageStatus = currentStageEntry?.status || "in_progress";

  // ── Key facts — shown in Card 1 ──
  const keyFacts = (data.key_facts || []).slice(0, 3);

  // ── Client lifecycle for Card 1 pill ──
  const lifecycleRaw = (data.workflow_stage?.stages || []).length > 0
    ? (wfStages.find((s) => s.key === "lead") ? "lead" : "active_client")
    : "lead";

  return (
    <div className="mb-5" data-testid="operational-cockpit">
      <JourneyBanner stage={data.workflow_stage} />

      {/* Primary scope label */}
      {shouldShowScopeContext && data.primary_scope_label && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3 bg-slate-50 rounded-lg px-3 py-1.5 border border-slate-200 w-fit" data-testid="cockpit-primary-scope">
          <Icons.Layers className="h-3.5 w-3.5 text-slate-400" />
          <span className="font-semibold text-slate-700">{data.primary_scope_label}</span>
          {data.primary_engagement_number && (
            <span className="text-slate-400 tabular-nums">· {data.primary_engagement_number}</span>
          )}
          <span className="text-slate-400">— highest priority workstream</span>
        </div>
      )}

      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-2">At a glance — the 3 most important questions</div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* CARD 1 — WHAT DO WE KNOW? */}
        <QuestionCard n="1" tone="slate" question="What do we know?" testid="cockpit-what-we-know">
          <div className="flex flex-col gap-2.5 flex-1">
            {/* Current state label */}
            <div className="flex items-start gap-2">
              <Icons.Info className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <div className="text-sm font-semibold text-slate-800 leading-snug" data-testid="cockpit-current-label">{cs.label}</div>
            </div>
            {/* Key facts */}
            {keyFacts.length > 0 && (
              <ul className="space-y-1">
                {keyFacts.map((f, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-500">
                    <Icons.Dot className="h-4 w-4 text-slate-300 shrink-0 -ml-1" />{f}
                  </li>
                ))}
              </ul>
            )}
            {/* Contextual note from reason */}
            {reason.factors?.[0] && !keyFacts.length && (
              <div className="text-[12px] text-slate-500">{reason.factors[0]}</div>
            )}
          </div>
          {/* AI Briefing button — moved from amber panel */}
          <button onClick={onViewBriefing} data-testid="cockpit-view-briefing"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-600 border border-slate-200 bg-white hover:bg-slate-50 rounded-lg px-3 py-2 transition-colors">
            <Icons.Sparkles className="h-3.5 w-3.5 text-indigo-500" />View AI Briefing<Icons.ExternalLink className="h-3 w-3" />
          </button>
        </QuestionCard>

        {/* CARD 2 — WHERE ARE WE NOW? */}
        <QuestionCard n="2" tone="green" question="Where are we now?" testid="cockpit-lifecycle-stage">
          <div className="flex items-start gap-2 flex-1">
            <Icons.MapPin className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-lg font-heading font-semibold text-slate-800 leading-tight" data-testid="cockpit-stage-label">{currentStageLabel}</div>
              {cs.effective_date && (
                <div className="text-xs font-medium text-emerald-700 mt-1">As of {String(cs.effective_date).slice(0, 10)}</div>
              )}
              {currentStageStatus === "in_progress" && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 inline-block" />In progress
                </div>
              )}
              {currentStageStatus === "complete" && (
                <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5">
                  <Icons.CheckCircle2 className="h-3 w-3" />Complete
                </div>
              )}
            </div>
          </div>
          {/* Goal context */}
          {goal.label && (
            <div className="mt-3 flex items-center gap-1.5 text-[11px] text-slate-400 border-t border-emerald-100 pt-2">
              <Icons.Target className="h-3.5 w-3.5 shrink-0" />
              <span>Goal: {goal.label}</span>
            </div>
          )}
        </QuestionCard>

        {/* CARD 3 — NEXT RECOMMENDED ACTION: 5-state machine */}
        <QuestionCard n="3" tone="orange" question="Next recommended action" testid="cockpit-next-action">

          {/* STATE 1 — Initial evaluation (drlData not yet returned) */}
          {drlLoading && (
            <div className="flex-1 flex flex-col items-center justify-center gap-2.5 py-3" data-testid="cockpit-card3-loading">
              <div className="h-5 w-5 rounded-full border-2 border-orange-200 border-t-orange-500 animate-spin" />
              <p className="text-sm text-slate-500 text-center">Evaluating next recommended action…</p>
              <div className="w-full space-y-1.5 mt-1">
                <div className="h-2.5 bg-slate-100 rounded-full animate-pulse w-3/4 mx-auto" />
                <div className="h-2.5 bg-slate-100 rounded-full animate-pulse w-1/2 mx-auto" />
              </div>
            </div>
          )}

          {/* STATE 2 — Post-resolve re-evaluation */}
          {resolving && (
            <div className="flex-1 flex flex-col items-center justify-center gap-2.5 py-3" data-testid="cockpit-card3-resolving">
              <div className="h-5 w-5 rounded-full border-2 border-orange-200 border-t-orange-500 animate-spin" />
              <p className="text-sm text-slate-600 font-medium text-center">Resolved — evaluating next recommended action…</p>
              <div className="w-full space-y-1.5 mt-1">
                <div className="h-2.5 bg-slate-100 rounded-full animate-pulse w-3/4 mx-auto" />
                <div className="h-2.5 bg-slate-100 rounded-full animate-pulse w-1/2 mx-auto" />
              </div>
            </div>
          )}

          {/* STATE 3 — DRL assessment error (distinct from no_material_blocker) */}
          {drlError && (
            <>
              <div className="flex items-center gap-1.5 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 mb-2.5" data-testid="cockpit-card3-error">
                <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0" />Assessment unavailable — showing operational default
              </div>
              <div className="flex items-start gap-2 mb-3 flex-1">
                <Icons.ClipboardList className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                <div className="text-sm font-semibold text-slate-800 leading-snug" data-testid="cockpit-next-label">{na.label}</div>
              </div>
              <button onClick={() => onPrimaryAction?.(na)} disabled={na.cta && na.cta.enabled === false}
                data-testid="cockpit-primary-cta"
                className="mt-auto inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors w-full">
                <Icons.Send className="h-4 w-4" />{na.cta?.label || na.label}
              </button>
            </>
          )}

          {/* STATE 4 — DRL active_blocker: Allison recommendation + Resolve or Go to Documents */}
          {drlActive && (
            <>
              <div className="flex items-start gap-2 mb-3 flex-1">
                <Icons.Sparkles className="h-5 w-5 text-indigo-500 shrink-0 mt-0.5" />
                <div>
                  <div className="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide mb-0.5">Allison recommends</div>
                  <div className="text-sm font-semibold text-slate-800 leading-snug" data-testid="cockpit-next-label">{drlData.primary_blocker}</div>
                  {drlData.recommended_action && drlData.recommended_action !== drlData.primary_blocker && (
                    <div className="text-[12px] text-slate-500 mt-1 leading-snug">{drlData.recommended_action}</div>
                  )}
                </div>
              </div>
              {/* Document blockers route to the governed Resolve Document Blocker panel,
                   not the generic disposition dialog and not a raw Documents navigation */}
              {isDocumentBlocker ? (
                <button
                  onClick={() => onDocumentAction?.()}
                  data-testid="cockpit-resolve-document-blocker-btn"
                  className="mt-auto inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors w-full"
                >
                  <Icons.ShieldAlert className="h-4 w-4" />Resolve Document Blocker
                </button>
              ) : (
                /* Resolve — canonical DRL disposition mechanism for factual/investigative blockers */
                <button
                  onClick={() => setResolveDialogOpen(true)}
                  data-testid="cockpit-resolve-btn"
                  className="mt-auto w-full inline-flex items-center justify-center gap-1.5 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors"
                >
                  <Icons.CheckCircle2 className="h-4 w-4" />Resolve
                </button>
              )}
            </>
          )}

          {/* STATE 5 — No material blocker: canonical cockpit next_action */}
          {drlClear && (
            na.action_code === "no_action_required" ? (
              /* Terminal state: completed engagement, no staff action required.
                 Backend supplies action_code="no_action_required" with cta=null.
                 Render informational completion state — no orange CTA, no Send icon. */
              <div className="flex-1 flex flex-col items-center justify-center gap-2 py-3" data-testid="cockpit-card3-terminal">
                <Icons.CheckCircle2 className="h-6 w-6 text-emerald-500" />
                <div className="text-sm font-semibold text-slate-700 text-center" data-testid="cockpit-next-label">
                  No action required
                </div>
                <div className="text-[12px] text-slate-400 text-center leading-snug px-2">
                  This engagement is complete and closed. A review request has been sent.
                </div>
              </div>
            ) : (
              /* Active action: orange operational CTA */
              <>
                <div className="flex items-start gap-2 mb-3 flex-1">
                  <Icons.ClipboardList className="h-5 w-5 text-orange-500 shrink-0 mt-0.5" />
                  <div className="text-sm font-semibold text-slate-800 leading-snug" data-testid="cockpit-next-label">{na.label}</div>
                </div>
                <button onClick={() => onPrimaryAction?.(na)} disabled={na.cta && na.cta.enabled === false}
                  data-testid="cockpit-primary-cta"
                  className="mt-auto inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors w-full">
                  <Icons.Send className="h-4 w-4" />{na.cta?.label || na.label}
                </button>
                {secondaryAction && (
                  <button onClick={() => onSecondaryAction?.(secondaryAction)} data-testid="cockpit-secondary-cta"
                    className="mt-2 inline-flex items-center justify-center gap-2 border border-slate-300 text-slate-600 hover:bg-slate-50 hover:border-slate-400 font-medium rounded-xl px-4 py-2 text-sm transition-colors w-full">
                    <Icons.ArrowRightCircle className="h-4 w-4 shrink-0" />{secondaryAction.label}
                  </button>
                )}
                {isEstimateObligation && (
                  <button onClick={() => onResolveObligation?.()} data-testid="cockpit-resolve-obligation-btn"
                    className="mt-2 inline-flex items-center justify-center gap-1.5 border border-amber-200 text-amber-700 hover:bg-amber-50 hover:border-amber-300 font-medium rounded-xl px-4 py-2 text-xs transition-colors w-full">
                    <Icons.AlertTriangle className="h-3.5 w-3.5 shrink-0" />Resolve Obligation
                  </button>
                )}
              </>
            )
          )}

        </QuestionCard>
      </div>

      {/* Other Active Workstreams */}
      <WorkstreamsSection workstreams={workstreams} navigate={navigate} />

      {/* DRL Resolve dialog — renders only when DRL is active, not resolving, and blocker is not document_missing */}
      {drlActive && !isDocumentBlocker && (
        <DispositionDialog
          open={resolveDialogOpen}
          onClose={() => setResolveDialogOpen(false)}
          blockerKey={drlData?.blocker_category}
          blockerTitle={drlData?.primary_blocker || "Case blocker"}
          clientId={clientId}
          engagementId={drlData?.scope_engagement_id || data?.primary_engagement_id}
          onDone={() => {
            setResolveDialogOpen(false);
            setResolving(true);
            onRefresh?.();
          }}
        />
      )}
    </div>
  );
}

/* CockpitRail — the three secondary reference cards (Routing Recommendation, Priority, Key Facts).
   Exported separately so the parent page can position them at the bottom of the Overview
   without changing the cockpit's internal logic or data. */
export function CockpitRail({ data, onViewRouting }) {
  if (!data) return null;
  const conf = CONF[data.confidence?.level] || CONF.medium;
  const priority = data.priority || {};
  const mgmt = data.management_review || {};
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="cockpit-rail">
      <RailCard title="Routing Recommendation" icon={Icons.Route} testid="cockpit-routing"
        action={<button onClick={onViewRouting} data-testid="cockpit-view-routing" className="text-[11px] font-semibold text-blue-600 hover:underline">Details</button>}>
        {data.recommended_primary_team ? (
          <>
            <div className="text-base font-semibold text-emerald-700" data-testid="cockpit-primary-team">Primary: {data.recommended_primary_team}</div>
            {data.collaborating_teams?.length > 0 && <div className="text-xs text-slate-500 mt-0.5">Collaborating: {data.collaborating_teams.join(", ")}</div>}
            <div className="mt-2">
              <div className="flex items-center justify-between text-[11px] mb-1"><span className="text-slate-400">Confidence</span><span className={CONF[String(data.routing_confidence || "").toLowerCase()]?.cls || "text-slate-500"}>{data.routing_confidence || "—"}</span></div>
              <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${CONF[String(data.routing_confidence || "").toLowerCase()]?.bar || "bg-slate-300"} ${CONF[String(data.routing_confidence || "").toLowerCase()]?.w || "w-1/2"}`} /></div>
            </div>
            {mgmt.required && <div className="mt-2 text-[11px] text-amber-700 flex items-center gap-1"><Icons.Flag className="h-3 w-3" />Management review recommended</div>}
          </>
        ) : <div className="text-sm text-slate-400">Confirm the engagement's U.S./Thai tax angle to route.</div>}
        {data.assigned_team && <div className="text-[11px] text-slate-400 mt-2 pt-2 border-t border-slate-100">Currently assigned: <span className="font-medium text-slate-600">{data.assigned_team}</span></div>}
      </RailCard>

      <RailCard title="Priority" icon={Icons.Flag} testid="cockpit-priority">
        <div className={`text-base font-semibold capitalize ${priority.level === "high" ? "text-rose-600" : "text-slate-700"}`} data-testid="cockpit-priority-level">
          {priority.level === "high" ? "High Priority" : `${priority.level || "Normal"} Priority`}
        </div>
        <div className="mt-2 space-y-1 text-xs">
          <div className="flex justify-between"><span className="text-slate-400">Confidence</span><span className={`font-semibold ${conf.cls}`} data-testid="cockpit-confidence">{conf.label}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Last activity</span><span className="text-slate-600">{priority.last_activity ? String(priority.last_activity).slice(0, 10) : "—"}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Next follow-up</span><span className="text-slate-600">{priority.next_follow_up ? String(priority.next_follow_up).slice(0, 10) : "—"}</span></div>
        </div>
      </RailCard>

      <RailCard title="Key Facts" icon={Icons.KeyRound} testid="cockpit-key-facts">
        {Array.isArray(data.key_facts) && data.key_facts.length ? (
          <ul className="space-y-1">
            {data.key_facts.map((f, i) => (
              <li key={i} className="text-[13px] text-slate-600 flex items-start gap-1.5"><Icons.Dot className="h-4 w-4 text-slate-300 shrink-0 -ml-1" />{f}</li>
            ))}
          </ul>
        ) : <div className="text-sm text-slate-400">No key facts captured yet.</div>}
      </RailCard>
    </div>
  );
}
